import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  Code, 
  Database, 
  Cloud, 
  Layers 
} from 'lucide-react';

// --- ANIMATION VARIANTS for Framer Motion ---
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// --- AnimatedSection Wrapper ---
const AnimatedSection = ({ children, id, className = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    return (
        <motion.section
            id={id}
            ref={ref}
            className={`py-24 md:py-32 overflow-hidden ${className}`}
            variants={sectionVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
        >
            {children}
        </motion.section>
    );
};

const AnimatedText = ({ text, className, variants, ...props }) => {
    const words = text.split(" ");
    return (
        <motion.p className={className} variants={variants} {...props}>
            {words.map((word, i) => (
                <motion.span key={i} className="inline-block mr-[0.25em]" variants={itemVariants}>
                    {word}
                </motion.span>
            ))}
        </motion.p>
    );
};

// --- TECHNOLOGY STACK SECTION ---
const TechIcon = ({ icon, name }) => (
    <motion.div variants={itemVariants} className="flex flex-col items-center gap-2 text-center group">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-full border border-slate-200 dark:border-gray-700 group-hover:border-cyan-400 dark:group-hover:border-cyan-500 transition-all duration-300 transform group-hover:scale-110 shadow-sm hover:shadow-md">
            {icon}
        </div>
        <p className="text-sm opacity-0 group-hover:opacity-100 transition-opacity text-slate-700 dark:text-gray-300 group-hover:text-cyan-500 dark:group-hover:text-cyan-300">{name}</p>
    </motion.div>
);

const TechnologiesSection = () => {
    const ReactLogo = () => <svg role="img" viewBox="0 0 113.8 102.3" className="w-8 h-8 text-cyan-500 dark:text-cyan-400"><g><circle cx="56.9" cy="51.1" r="10.2" fill="currentColor"></circle><ellipse transform="rotate(-60 56.9 51.1)" cx="56.9" cy="51.1" rx="49.9" ry="19.2" fill="none" stroke="currentColor" strokeWidth="5"></ellipse><ellipse transform="rotate(60 56.9 51.1)" cx="56.9" cy="51.1" rx="49.9" ry="19.2" fill="none" stroke="currentColor" strokeWidth="5"></ellipse><ellipse transform="rotate(0 56.9 51.1)" cx="56.9" cy="51.1" rx="49.9" ry="19.2" fill="none" stroke="currentColor" strokeWidth="5"></ellipse></g></svg>;
    const NodeLogo = () => <svg role="img" viewBox="0 0 100 100" className="w-8 h-8 text-green-500 dark:text-green-400"><path fill="currentColor" d="M89.7,46.2L54.4,26.4c-2-1.2-4.5-1.2-6.5,0L12.3,46.2c-2,1.2-3.2,3.3-3.2,5.6v39.6c0,2.3,1.3,4.4,3.2,5.6l35.6,19.8c1,0.6,2.1,0.8,3.2,0.8s2.2-0.3,3.2-0.8l35.6-19.8c2-1.2,3.2-3.3,3.2-5.6V51.8C92.9,49.5,91.6,47.4,89.7,46.2z M51.2,85.1L27,71.2v-25l24.2,13.9V85.1z M51.2,55.8L27,41.9l24.2-13.9l24.2,13.9L51.2,55.8z M75.4,71.2L51.2,85.1V60.1l24.2-13.9V71.2z"></path></svg>;

    // SVGs for WordPress, Next.js, NestJS, Wireshark, Git, Swagger, Figma, Adobe, Google Analytics, SEO Tools
    const WordPressLogo = () => (
        <svg viewBox="0 0 128 128" className="w-8 h-8 text-blue-700 dark:text-blue-400" fill="currentColor">
            <circle cx="64" cy="64" r="64" fill="currentColor" opacity="0.1"/>
            <path d="M64 12C34.4 12 10 36.4 10 66c0 29.6 24.4 54 54 54s54-24.4 54-54C118 36.4 93.6 12 64 12zm0 104c-27.6 0-50-22.4-50-50S36.4 16 64 16s50 22.4 50 50-22.4 50-50 50zm0-96C37.5 20 16 41.5 16 68c0 26.5 21.5 48 48 48s48-21.5 48-48c0-26.5-21.5-48-48-48zm0 92c-24.3 0-44-19.7-44-44s19.7-44 44-44 44 19.7 44 44-19.7 44-44 44zm-2.2-68.7c-2.7 0-5.3.2-7.8.7l17.2 47.2 10.2-28.1c.1-.3.2-.6.2-.9 0-.3-.1-.6-.2-.9l-8.2-22.7c-3.5-1.1-7.2-1.7-11.4-1.7zm-17.7 3.6c-10.1 6.7-16.7 18.1-16.7 31.1 0 13.2 6.8 24.7 17.2 31.3l-14.1-38.7c-.2-.5-.3-1-.3-1.5 0-.5.1-1 .3-1.5l13.6-20.7zm37.8 1.2c-1.7-.2-3.5-.3-5.3-.3-2.2 0-4.3.2-6.4.5l17.2 47.2 10.2-28.1c.1-.3.2-.6.2-.9 0-.3-.1-.6-.2-.9l-8.2-22.7c-2.1-.5-4.3-.8-6.5-.8zm-19.7 2.2c-2.1.5-4.1 1.2-6 2.1l14.1 38.7c.2.5.3 1 .3 1.5 0 .5-.1 1-.3 1.5l-13.6 20.7c10.1-6.7 16.7-18.1 16.7-31.1 0-13.2-6.8-24.7-17.2-31.3zm-8.7 3.7c-7.2 5.2-11.8 13.7-11.8 23.2 0 9.5 4.6 18 11.8 23.2l-10.1-28.1c-.1-.3-.2-.6-.2-.9 0-.3.1-.6.2-.9l10.1-16.5zm47.7 2.2c-2.1-.5-4.3-.8-6.5-.8-2.2 0-4.3.2-6.4.5l17.2 47.2 10.2-28.1c.1-.3.2-.6.2-.9 0-.3-.1-.6-.2-.9l-8.2-22.7zm-19.7 2.2c-2.1.5-4.1 1.2-6 2.1l14.1 38.7c.2.5.3 1 .3 1.5 0 .5-.1 1-.3 1.5l-13.6 20.7c10.1-6.7 16.7-18.1 16.7-31.1 0-13.2-6.8-24.7-17.2-31.3z"/>
        </svg>
    );
    const NextJsLogo = () => (
        <svg viewBox="0 0 32 32" className="w-8 h-8 text-black dark:text-white" fill="none">
            <circle cx="16" cy="16" r="16" fill="currentColor" opacity="0.07"/>
            <path d="M16 6c5.523 0 10 4.477 10 10 0 2.21-.72 4.25-1.93 5.89l-7.32-12.68A9.96 9.96 0 0 0 16 6zm-8.94 4.94A10 10 0 0 1 16 6c1.7 0 3.3.42 4.7 1.16l-12.68 7.32A9.96 9.96 0 0 1 7.06 10.94zm-1.16 4.7l12.68-7.32A10 10 0 0 1 26 16c0 1.7-.42 3.3-1.16 4.7l-7.32-12.68A9.96 9.96 0 0 1 5.9 15.64zm1.16 4.7l7.32 12.68A10 10 0 0 1 6 16c0-1.7.42-3.3 1.16-4.7zm4.7 1.16l12.68-7.32A10 10 0 0 1 16 26c-1.7 0-3.3-.42-4.7-1.16l7.32-12.68A9.96 9.96 0 0 1 15.64 21.86z" fill="currentColor"/>
            <path d="M22 22L10 10" stroke="currentColor" strokeWidth="2"/>
        </svg>
    );
    const NestJsLogo = () => (
        <svg viewBox="0 0 256 256" className="w-8 h-8 text-red-600 dark:text-red-400" fill="currentColor">
            <g>
                <path d="M186.2 186.6c-6.7 6.7-14.2 12.2-22.4 16.2 2.7-3.2 5.2-6.5 7.5-10 8.6-12.8 14.2-27.7 15.7-43.7 1.2-12.7-.2-25.2-4.2-37.2-3.8-11.2-10.2-21.3-18.7-29.8-8.5-8.5-18.6-14.9-29.8-18.7-12-4-24.5-5.4-37.2-4.2-16 1.5-30.9 7.1-43.7 15.7-3.5 2.3-6.8 4.8-10 7.5 4-8.2 9.5-15.7 16.2-22.4C61.7 41.7 91.7 32 128 32s66.3 9.7 90.6 34c24.3 24.3 34 54.3 34 90.6s-9.7 66.3-34 90.6z"/>
                <path d="M128 224c-53 0-96-43-96-96 0-53 43-96 96-96s96 43 96 96c0 53-43 96-96 96zm0-176c-44.2 0-80 35.8-80 80 0 44.2 35.8 80 80 80 44.2 0 80-35.8 80-80 0-44.2-35.8-80-80-80z"/>
            </g>
        </svg>
    );
    const WiresharkLogo = () => (
        <svg viewBox="0 0 48 48" className="w-8 h-8 text-blue-400 dark:text-blue-300" fill="none">
            <circle cx="24" cy="24" r="24" fill="currentColor" opacity="0.08"/>
            <path d="M8 36c8-16 24-16 32 0" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M24 12v24" stroke="currentColor" strokeWidth="2" strokeDasharray="4 2"/>
        </svg>
    );
    const GitLogo = () => (
        <svg viewBox="0 0 32 32" className="w-8 h-8 text-orange-500 dark:text-orange-400" fill="currentColor">
            <circle cx="16" cy="16" r="16" fill="currentColor" opacity="0.08"/>
            <path d="M29.3 14.9L17.1 2.7c-1.1-1.1-2.8-1.1-3.9 0l-2.2 2.2 3.2 3.2c1.1-.4 2.4-.1 3.3.8 1 1 1.2 2.5.7 3.7l3.1 3.1c1.2-.5 2.7-.3 3.7.7 1.4 1.4 1.4 3.6 0 5-1.4 1.4-3.6 1.4-5 0-.9-.9-1.2-2.2-.7-3.4l-2.9-2.9v7.6c.3.1.6.3.8.5 1.1 1.1 1.1 2.8 0 3.9-1.1 1.1-2.8 1.1-3.9 0-1.1-1.1-1.1-2.8 0-3.9.3-.3.7-.6 1.1-.7v-7.7c-.4-.1-.8-.4-1.1-.7-1.1-1.1-1.1-2.8 0-3.9 1.1-1.1 2.8-1.1 3.9 0 .3.3.5.6.6 1l3.2-3.2-2.2-2.2c-1.1-1.1-2.8-1.1-3.9 0L2.7 17.1c-1.1 1.1-1.1 2.8 0 3.9l12.2 12.2c1.1 1.1 2.8 1.1 3.9 0l12.2-12.2c1.1-1.1 1.1-2.8 0-3.9z"/>
        </svg>
    );
    const SwaggerLogo = () => (
        <svg viewBox="0 0 48 48" className="w-8 h-8 text-green-500 dark:text-green-400" fill="none">
            <circle cx="24" cy="24" r="24" fill="currentColor" opacity="0.08"/>
            <ellipse cx="24" cy="24" rx="16" ry="10" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="16" cy="24" r="2" fill="currentColor"/>
            <circle cx="32" cy="24" r="2" fill="currentColor"/>
        </svg>
    );
    const FigmaLogo = () => (
        <svg viewBox="0 0 48 48" className="w-8 h-8" fill="none">
            <circle cx="24" cy="24" r="24" fill="#0ACF83" opacity="0.08"/>
            <g>
                <circle cx="24" cy="34" r="6" fill="#A259FF"/>
                <circle cx="24" cy="24" r="6" fill="#1ABCFE"/>
                <circle cx="24" cy="14" r="6" fill="#0ACF83"/>
                <circle cx="34" cy="24" r="6" fill="#FF7262"/>
                <circle cx="14" cy="24" r="6" fill="#F24E1E"/>
            </g>
        </svg>
    );
    const AdobeLogo = () => (
        <svg viewBox="0 0 48 48" className="w-8 h-8" fill="none">
            <circle cx="24" cy="24" r="24" fill="#FF0000" opacity="0.08"/>
            <path d="M10 38L24 10l14 28h-7l-7-16-7 16h-7z" fill="#FF0000"/>
        </svg>
    );
    const GoogleAnalyticsLogo = () => (
        <svg viewBox="0 0 48 48" className="w-8 h-8" fill="none">
            <circle cx="24" cy="24" r="24" fill="#FFA726" opacity="0.08"/>
            <rect x="10" y="30" width="6" height="8" rx="3" fill="#FFA726"/>
            <rect x="20" y="18" width="6" height="20" rx="3" fill="#FB8C00"/>
            <rect x="30" y="10" width="6" height="28" rx="3" fill="#F57C00"/>
        </svg>
    );
    const SeoLogo = () => (
        <svg viewBox="0 0 48 48" className="w-8 h-8 text-cyan-600 dark:text-cyan-400" fill="none">
            <circle cx="24" cy="24" r="24" fill="currentColor" opacity="0.08"/>
            <path d="M24 14a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm0 0v-4m0 24v-4m10-10h4m-24 0h4m13.07-7.07l2.83-2.83m-18.38 18.38l2.83-2.83m0-12.72l-2.83-2.83m18.38 18.38l-2.83-2.83" stroke="currentColor" strokeWidth="2"/>
        </svg>
    );

    return (
        <AnimatedSection id="tech" className="bg-white dark:bg-black text-slate-900 dark:text-white">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <AnimatedText text="The Tools We Master." className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white" variants={sectionVariants}/>
                    <p className="text-lg text-slate-600 dark:text-gray-400 mt-4">We use the best and latest technologies to build future-proof solutions.</p>
                </div>
                <motion.div
                    variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
                    className="grid grid-cols-4 md:grid-cols-8 gap-8 md:gap-10 max-w-5xl mx-auto"
                >
                    <TechIcon icon={<ReactLogo />} name="React" />
                    <TechIcon icon={<NextJsLogo />} name="Next.js" />
                    <TechIcon icon={<NodeLogo />} name="Node.js" />
                    <TechIcon icon={<NestJsLogo />} name="NestJS" />
                    <TechIcon icon={<WordPressLogo />} name="WordPress" />
                    <TechIcon icon={<Code size={32} className="text-blue-500 dark:text-blue-400" />} name="Python" />
                    <TechIcon icon={<Database size={32} className="text-yellow-500 dark:text-yellow-400" />} name="SQL/NoSQL" />
                    <TechIcon icon={<Cloud size={32} className="text-orange-500 dark:text-orange-400" />} name="AWS/GCP" />
                    <TechIcon icon={<Layers size={32} className="text-purple-500 dark:text-purple-400" />} name="Docker" />
                    <TechIcon icon={<WiresharkLogo />} name="Wireshark" />
                    <TechIcon icon={<GitLogo />} name="Git" />
                    <TechIcon icon={<SwaggerLogo />} name="Swagger" />
                    <TechIcon icon={<FigmaLogo />} name="Figma" />
                    <TechIcon icon={<AdobeLogo />} name="Adobe CC" />
                    <TechIcon icon={<GoogleAnalyticsLogo />} name="Google Analytics" />
                    <TechIcon icon={<SeoLogo />} name="SEO Tools" />
                </motion.div>
            </div>
        </AnimatedSection>
    );
};

export default TechnologiesSection;
